import React from 'react';
import type { FC } from 'react';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { ButtonItem } from '@atlaskit/menu';

export type ExpandableSubMenuItemItemType = {
	id: string;
	label: string;
	tooltip?: string;
	url: string;
	badgeLabel?: string;
	styleClass?: string; // Some Connect apps have a styleClass to apply custom styles
};

export type ExpandableSubMenuItemProps = {
	label: string | React.ReactNode;
	items: ExpandableSubMenuItemItemType[];
	isSubMenuOpen: boolean;
	onMouseEnter: () => void;
	onClick: () => void;
};

// this is to keep consistent with ContentTools PopupMenuGroup
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledDropdownItemGroup = styled(DropdownItemGroup)({
	minWidth: '220px',
	maxWidth: '800px',
});

// styled(DropdownItem) doesn't apply css to component
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemLabelContainer = styled.span({
	display: 'flex',
	justifyContent: 'space-between',
});

const badgeContainerStyles = xcss({
	paddingLeft: 'space.075',
});

export const ExpandableSubMenuItem: FC<ExpandableSubMenuItemProps> = ({
	label,
	items,
	isSubMenuOpen,
	onMouseEnter,
	onClick,
}) => {
	if (!items.length) return null;
	return (
		<DropdownMenu
			placement="left-start"
			isOpen={isSubMenuOpen}
			trigger={({ triggerRef, ...triggerProps }) => (
				<ButtonItem
					{...triggerProps}
					ref={triggerRef}
					// @ts-ignore
					onMouseEnter={onMouseEnter}
					onClick={onClick}
					iconAfter={<ChevronRightIcon label="" />}
				>
					{label}
				</ButtonItem>
			)}
		>
			<StyledDropdownItemGroup>
				{items.map((item) => (
					<DropdownItem key={item.id} href={item.url} UNSAFE_shouldDisableRouterLink>
						<DropdownItemLabelContainer>
							<span>{item.label}</span>
							{item.badgeLabel ? (
								<Box xcss={badgeContainerStyles}>
									<Badge>{item.badgeLabel}</Badge>
								</Box>
							) : null}
						</DropdownItemLabelContainer>
					</DropdownItem>
				))}
			</StyledDropdownItemGroup>
		</DropdownMenu>
	);
};
